import { useEffect, useState, useRef } from "react";
import { ref, getDatabase, get } from "firebase/database";

/**
 * Draws calibration points (circles) and gaze vectors on an SVG
 * representing the display area.
 *
 * - Circles mark calibration target positions
 * - Green lines = left eye gaze vector (target → where eye looked)
 * - Red lines = right eye gaze vector (target → where eye looked)
 */
export default function CalibrationView({ evalId }) {
  const [calibration, setCalibration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!evalId) return;

    get(ref(getDatabase(), `evals/${evalId}/calibration`))
      .then((snapshot) => {
        if (snapshot.exists()) {
          setCalibration(snapshot.val());
        } else {
          setError("No calibration data found for this eval.");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [evalId]);

  if (loading) return <p>Loading calibration data...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!calibration?.calibration_points) return <p>No calibration points available.</p>;

  const points = calibration.calibration_points;
  const WIDTH = 800;
  const HEIGHT = 500;

  function toSvg(normalized) {
    return [normalized[0] * WIDTH, normalized[1] * HEIGHT];
  }

  const isValid = (validity) =>
    validity === "validity_valid_and_used";

  return (
    <div ref={containerRef}>
      <div className="mb-4">
        <span className="text-sm text-gray-500">
          Status: <span className="font-medium text-gray-900">{calibration.status?.replace(/_/g, " ")}</span>
        </span>
        <div className="flex gap-4 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 bg-green-500" /> Left eye
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 bg-red-500" /> Right eye
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full border-2 border-gray-800 bg-transparent" /> Target
          </span>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full max-w-4xl border border-gray-300 rounded-lg"
        style={{ backgroundColor: "#5B9BD5" }}
      >
        {points.map((point, i) => {
          const [tx, ty] = toSvg(point.position_on_display_area);
          const sample = point.sample;
          const leftValid = isValid(sample.leftEyeValidity);
          const rightValid = isValid(sample.rightEyeValidity);

          return (
            <g key={i}>
              {/* Left eye vector (green) */}
              {leftValid && (() => {
                const [lx, ly] = toSvg(sample.leftEyePoint);
                return (
                  <line
                    x1={tx} y1={ty} x2={lx} y2={ly}
                    stroke="#22c55e" strokeWidth={2}
                  />
                );
              })()}

              {/* Right eye vector (red) */}
              {rightValid && (() => {
                const [rx, ry] = toSvg(sample.rightEyePoint);
                return (
                  <line
                    x1={tx} y1={ty} x2={rx} y2={ry}
                    stroke="#ef4444" strokeWidth={2}
                  />
                );
              })()}

              {/* Calibration target point */}
              <circle
                cx={tx} cy={ty} r={12}
                fill="none" stroke="#1f2937" strokeWidth={2}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
